import React, { useEffect, useState } from 'react'
import { Modal, ModalHeader, ModalBody, Button, Label, Form, InputGroup, InputGroupText } from 'reactstrap'
import { useForm, Controller } from 'react-hook-form'
import Select from 'react-select'
import { Eye, EyeOff } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { getUserData } from '@utils'
import toast from 'react-hot-toast'
import {
  createCompanyIskurInfo,
  toggleCreatePopup,
  getCompaniesForSelect,
  getUsersForSelect
} from './store'

const CreatePopup = () => {
  const dispatch = useDispatch()
  const { createPopupOpen, companies, users, loading } = useSelector(s => s.companyIskurInfo)
  const authStore = useSelector((state) => state.auth)
  const user = getUserData()
  const [showPassword, setShowPassword] = useState(false)
  
  const [localSelectedCompanyGroups, setLocalSelectedCompanyGroups] = useState([])
  
  const { control, handleSubmit, reset, formState: { errors }, setValue } = useForm({
    defaultValues: { iskurCode: '', password: '', companyIds: [], userIds: [] }
  })

  useEffect(() => {
    if (createPopupOpen && authStore.selectedCompanyGroup) {
      setLocalSelectedCompanyGroups([authStore.selectedCompanyGroup])
    }
  }, [createPopupOpen, authStore.selectedCompanyGroup])

  useEffect(() => {
    if (localSelectedCompanyGroups.length > 0) {
      dispatch(getCompaniesForSelect(localSelectedCompanyGroups))
      dispatch(getUsersForSelect(localSelectedCompanyGroups))
    }
  }, [dispatch, localSelectedCompanyGroups])

  const onSubmit = data => {
    if (localSelectedCompanyGroups.length === 0) {
      toast.error('Lütfen önce bir şirket grubu seçiniz')
      return
    }

    dispatch(createCompanyIskurInfo({
      iskurCode: data.iskurCode,
      password: data.password,
      companyGroupIds: localSelectedCompanyGroups,
      companyIds: data.companyIds.map(c => c.value),
      userIds: data.userIds.map(u => u.value)
    }))
    reset()
    setShowPassword(false)
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }
  const customStyles = {
    container: (provided) => ({
      ...provided,
      minWidth: '220px'
    }),
    control: (provided) => ({
      ...provided,
      minWidth: '220px'
    })
  }

  const customFilterOption = (option, inputValue) => {
    return option.label.toLocaleLowerCase('tr').includes(inputValue.toLocaleLowerCase('tr'))
  }

  const handleCompanyGroupChange = (selectedOptions) => {
    const selectedValues = selectedOptions ? selectedOptions.map(option => option.value) : []
    setLocalSelectedCompanyGroups(selectedValues)
    
    setValue('companyIds', [])
    setValue('userIds', [])
  }

  return (
    <Modal isOpen={createPopupOpen} toggle={() => dispatch(toggleCreatePopup(false))}>
      <ModalHeader toggle={() => dispatch(toggleCreatePopup(false))}>
        Yeni İŞKUR Bilgisi
      </ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-2">
            <Label>İŞKUR Kodu <span className="text-danger">*</span></Label>
            <Controller
              name="iskurCode"
              control={control}
              rules={{ required: 'İŞKUR kodu gereklidir' }}
              render={({ field }) => (
                <input 
                  className={`form-control ${errors.iskurCode ? 'is-invalid' : ''}`}
                  placeholder="İŞKUR kodunu giriniz"
                  {...field} 
                />
              )}
            />
            {errors.iskurCode && <div className="invalid-feedback">{errors.iskurCode.message}</div>}
          </div>

          <div className="mb-2">
            <Label>Şifre <span className="text-danger">*</span></Label>
            <Controller
              name="password"
              control={control}
              rules={{ required: 'Şifre gereklidir' }}
              render={({ field }) => (
                <InputGroup>
                  <input 
                    type={showPassword ? 'text' : 'password'}
                    className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                    placeholder="Şifrenizi giriniz"
                    {...field} 
                  />
                  <InputGroupText 
                    style={{ cursor: 'pointer' }}
                    onClick={togglePasswordVisibility}
                    title={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </InputGroupText>
                </InputGroup>
              )}
            />
            {errors.password && <div className="invalid-feedback d-block">{errors.password.message}</div>}
          </div>

          <div className="mb-2">
            <Label>Şirket Grupları <span className="text-danger">*</span></Label>
            <Select
              isMulti
              isClearable={false}
              options={user?.companyGroupList}
              onChange={handleCompanyGroupChange}
              value={user?.companyGroupList?.filter(x => localSelectedCompanyGroups.includes(x.value))}
              className='react-select'
              classNamePrefix='select'
              styles={customStyles}
              filterOption={customFilterOption}
              placeholder="Gruplar seçiniz..."
            />
          </div>

          <div className="mb-2">
            <Label>Sicil No <span className="text-danger">*</span></Label>
            {localSelectedCompanyGroups.length === 0 ? (
              <div className="text-muted p-2 border rounded">
                <small>Sicil No seçebilmek için önce yukarıdan gruplar seçiniz.</small>
              </div>
            ) : (
              <Controller
                name="companyIds"
                control={control}
                rules={{ required: 'En az bir şirket seçmelisiniz' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    isMulti
                    placeholder="Şirket seçiniz..."
                    options={(companies || []).map(c => ({ value: c.value, label: c.label }))}
                    onChange={vals => field.onChange(vals || [])}
                    noOptionsMessage={() => "Seçilen gruplarda şirket bulunamadı"}
                    className={errors.companyIds ? 'is-invalid' : ''}
                  />
                )}
              />
            )}
            {errors.companyIds && <div className="invalid-feedback d-block">{errors.companyIds.message}</div>}
          </div>

          <div className="mb-3">
            <Label>Kullanıcı Seç <span className="text-danger">*</span></Label>
            {localSelectedCompanyGroups.length === 0 ? (
              <div className="text-muted p-2 border rounded">
                <small>Kullanıcı seçebilmek için önce yukarıdan gruplar seçiniz.</small>
              </div>
            ) : (
              <Controller
                name="userIds"
                control={control}
                rules={{ required: 'En az bir kullanıcı seçmelisiniz' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    isMulti
                    placeholder="Kullanıcı seçiniz..."
                    options={(users || []).map(u => ({ value: u.id, label: u.firstAndLastName }))}
                    onChange={vals => field.onChange(vals || [])}
                    noOptionsMessage={() => "Seçilen gruplarda kullanıcı bulunamadı"}
                    className={errors.userIds ? 'is-invalid' : ''}
                  />
                )}
              />
            )}
            {errors.userIds && <div className="invalid-feedback d-block">{errors.userIds.message}</div>}
          </div>

          <div className="d-flex justify-content-end">
            <Button color="primary" type="submit" disabled={loading} className="me-1">
              {loading ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
            <Button outline onClick={() => {
              dispatch(toggleCreatePopup(false))
              setShowPassword(false)
              setLocalSelectedCompanyGroups([])
              reset()
            }}>
              İptal
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  )
}

export default CreatePopup